import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Card, Etiqueta, Meta, Pilula, cores } from "../../relatorio/styles";
import { descricaoDoMetodo, linkParaReivindicar, situacaoDoPedido } from "../pages/acesso";
import type { CategoriaNoGoogle, OpcaoDeVerificacao, PedidoDeAcesso, Reivindicacao } from "../types";

const Opcao = styled.button<{ $ativo?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  border: 1px solid ${({ $ativo }) => ($ativo ? cores.blue : cores.line)};
  background: ${({ $ativo }) => ($ativo ? cores.blueTint : "#fff")};
  border-radius: 14px;
  padding: 10px 14px;
  font: inherit;
  cursor: pointer;
  min-width: 160px;
  &:hover {
    border-color: ${cores.blue};
  }
  .titulo {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 14px;
  }
`;

const Campo = styled.input`
  border: 1px solid ${cores.line};
  border-radius: 1000px;
  padding: 11px 16px;
  font: inherit;
  font-size: 15px;
  outline: none;
  background: #fff;
  min-width: 200px;
  letter-spacing: 0.08em;
`;

const tituloDaOpcao = (opcao: OpcaoDeVerificacao): string => {
  switch (opcao.method) {
    case "SMS":
      return "SMS";
    case "PHONE_CALL":
      return "Ligação";
    case "EMAIL":
      return "E-mail";
    case "ADDRESS":
      return "Carta pelo correio";
    case "AUTO":
      return "Automática";
    default:
      return opcao.method;
  }
};

const destinoDaOpcao = (opcao: OpcaoDeVerificacao): string => {
  if (opcao.phoneNumber) return opcao.phoneNumber;
  if (opcao.emailDomain) return `${opcao.emailUser ?? "…"}@${opcao.emailDomain}`;
  if (opcao.method === "ADDRESS") return "até 14 dias";
  return "";
};

type Props = {
  reivindicacao: Reivindicacao;
  onIniciar: (dados: { method: string; phoneNumber?: string | null; emailAddress?: string | null }) => Promise<PedidoDeAcesso | null>;
  onConcluir: (pin: string) => Promise<PedidoDeAcesso | null>;
  onRecarregarOpcoes: () => Promise<void>;
  onTentarDeNovo?: (categoryId?: string) => void;
  onBuscarCategorias?: (texto: string) => Promise<CategoriaNoGoogle[]>;
};

/**
 * Depois de reivindicar a ficha na conta gestora: escolher como o Google manda
 * o código, pedir o envio e digitar o código que o negócio recebeu.
 */
export const Verificacao = ({ reivindicacao, onIniciar, onConcluir, onRecarregarOpcoes, onTentarDeNovo, onBuscarCategorias }: Props) => {
  const { pedido, opcoes, erro, faltaCategoria } = reivindicacao;
  const [buscaDeCategoria, setBuscaDeCategoria] = useState("");
  const [categorias, setCategorias] = useState<CategoriaNoGoogle[]>([]);
  const [categoria, setCategoria] = useState<CategoriaNoGoogle | null>(null);
  const temporizador = useRef<number | null>(null);

  useEffect(() => {
    if (!onBuscarCategorias) return undefined;
    if (temporizador.current) window.clearTimeout(temporizador.current);
    const termo = buscaDeCategoria.trim();
    if (termo.length < 3) {
      setCategorias([]);
      return undefined;
    }
    temporizador.current = window.setTimeout(async () => setCategorias(await onBuscarCategorias(termo)), 400);
    return () => {
      if (temporizador.current) window.clearTimeout(temporizador.current);
    };
  }, [buscaDeCategoria, onBuscarCategorias]);
  const [escolhida, setEscolhida] = useState<OpcaoDeVerificacao | null>(null);
  const [usuarioDoEmail, setUsuarioDoEmail] = useState("");
  const [pin, setPin] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const situacao = situacaoDoPedido(pedido);
  const concluido = pedido.status === "CONCLUIDO";
  const codigoEnviado = pedido.status === "VERIFICACAO_ENVIADA";
  const falhou = pedido.status === "ERRO";

  const enviar = async () => {
    if (!escolhida) return;
    setEnviando(true);
    const emailAddress = escolhida.method === "EMAIL" && escolhida.emailDomain
      ? `${escolhida.emailEditable && usuarioDoEmail.trim() ? usuarioDoEmail.trim() : escolhida.emailUser ?? ""}@${escolhida.emailDomain}`
      : null;
    await onIniciar({ method: escolhida.method, phoneNumber: escolhida.phoneNumber, emailAddress });
    setEnviando(false);
  };

  const confirmar = async () => {
    if (pin.trim().length < 4) return;
    setConfirmando(true);
    await onConcluir(pin.trim());
    setConfirmando(false);
  };

  return (
    <Card $fundo={concluido ? cores.greenTint : falhou ? cores.redTint : cores.blueTint} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15 }}>
          {concluido
            ? `“${pedido.profileName}” verificada: ${pedido.managerEmail} é dona`
            : falhou
              ? `“${pedido.profileName}”: o Google recusou a reivindicação`
              : `“${pedido.profileName}” reivindicada para ${pedido.managerEmail}`}
        </span>
        <Etiqueta $cor={situacao.cor}>{situacao.rotulo}</Etiqueta>
      </div>

      {pedido.errorMessage && <Meta style={{ color: cores.red }}>{pedido.errorMessage}</Meta>}
      {erro && erro !== pedido.errorMessage && <Meta style={{ color: cores.red }}>{erro}</Meta>}

      {concluido && (
        <Meta>A ficha já aparece na conta gestora como verificada. Ela entra na próxima importação de perfis.</Meta>
      )}

      {falhou && faltaCategoria && onBuscarCategorias && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Meta>O Google exige uma categoria principal para criar esta ficha e a busca não trouxe nenhuma. Escolha a categoria do negócio:</Meta>
          <Campo style={{ letterSpacing: 0, minWidth: 260 }} placeholder="Buscar categoria (ex.: campo de futebol)" aria-label="Buscar categoria" value={buscaDeCategoria} onChange={(evento) => { setBuscaDeCategoria(evento.target.value); setCategoria(null); }} />
          {categorias.length > 0 && !categoria && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {categorias.slice(0, 8).map((item) => (
                <Opcao key={item.gcid} type="button" onClick={() => setCategoria(item)} style={{ minWidth: 0 }}>
                  <span className="titulo">{item.category}</span>
                </Opcao>
              ))}
            </div>
          )}
          {categoria && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Etiqueta $cor="azul">{categoria.category}</Etiqueta>
              {onTentarDeNovo && (
                <Pilula as="button" type="button" onClick={() => onTentarDeNovo(categoria.gcid)} style={{ padding: "9px 18px", fontSize: 13 }}>
                  Reivindicar com esta categoria
                </Pilula>
              )}
            </div>
          )}
        </div>
      )}
      {falhou && !faltaCategoria && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Meta>A ficha não foi criada na conta gestora. Corrija o que o Google apontou, tente de novo ou reivindique pelo Maps com a conta gestora.</Meta>
          {onTentarDeNovo && (
            <Pilula as="button" type="button" $tipo="contorno" onClick={() => onTentarDeNovo()} style={{ padding: "7px 14px", fontSize: 12 }}>
              Tentar reivindicar de novo
            </Pilula>
          )}
          <Pilula $tipo="contorno" href={linkParaReivindicar(pedido.placeId, pedido.profileName)} target="_blank" rel="noopener noreferrer" style={{ padding: "7px 14px", fontSize: 12 }}>
            Reivindicar pelo Maps
          </Pilula>
        </div>
      )}

      {!concluido && !falhou && !codigoEnviado && (
        <>
          <Meta>Falta a verificação do Google. Escolha por onde o negócio recebe o código; o cliente te passa o código e você digita aqui.</Meta>
          {opcoes.length === 0 ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Meta>O Google ainda não devolveu opções de verificação para esta ficha.</Meta>
              <Pilula as="button" type="button" $tipo="contorno" onClick={onRecarregarOpcoes} style={{ padding: "7px 14px", fontSize: 12 }}>
                Tentar de novo
              </Pilula>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {opcoes.map((opcao) => (
                <Opcao key={`${opcao.method}-${opcao.phoneNumber ?? opcao.emailDomain ?? ""}`} type="button" $ativo={escolhida === opcao} onClick={() => setEscolhida(opcao)}>
                  <span className="titulo">{tituloDaOpcao(opcao)}</span>
                  <Meta>{destinoDaOpcao(opcao)}</Meta>
                </Opcao>
              ))}
            </div>
          )}
          {escolhida?.method === "EMAIL" && escolhida.emailEditable && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Campo style={{ letterSpacing: 0 }} placeholder={escolhida.emailUser ?? "usuario"} value={usuarioDoEmail} onChange={(evento) => setUsuarioDoEmail(evento.target.value)} aria-label="Usuário do e-mail" />
              <Meta>{`@${escolhida.emailDomain}`}</Meta>
            </div>
          )}
          {escolhida && (
            <div>
              <Pilula as="button" type="button" onClick={enviar} disabled={enviando} style={{ padding: "10px 20px", fontSize: 14 }}>
                {enviando ? "Pedindo ao Google…" : `Enviar código ${descricaoDoMetodo(escolhida.method)}`}
              </Pilula>
            </div>
          )}
        </>
      )}

      {!concluido && codigoEnviado && (
        <>
          <Meta>{`O Google enviou o código ${descricaoDoMetodo(pedido.verificationMethod)}${pedido.verificationTarget ? ` para ${pedido.verificationTarget}` : ""}. Peça ao cliente e digite abaixo.`}</Meta>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Campo inputMode="numeric" placeholder="Código recebido" aria-label="Código de verificação" value={pin} onChange={(evento) => setPin(evento.target.value)} />
            <Pilula as="button" type="button" onClick={confirmar} disabled={confirmando || pin.trim().length < 4} style={{ padding: "10px 20px", fontSize: 14 }}>
              {confirmando ? "Confirmando…" : "Confirmar código"}
            </Pilula>
            <Pilula as="button" type="button" $tipo="contorno" onClick={onRecarregarOpcoes} style={{ padding: "8px 14px", fontSize: 12 }}>
              Enviar de novo por outro meio
            </Pilula>
          </div>
        </>
      )}
    </Card>
  );
};
